import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { User, AuthError } from '@supabase/supabase-js'
import { supabase, clearSupabaseSessionCache, getAuthStorageKey } from '../lib/supabase'
import { getUserProfile, type UserProfile } from '../lib/profileService'

interface AuthContextType {
  user: User | null
  profile: UserProfile | null
  loading: boolean
  refreshProfile: () => Promise<UserProfile | null>
  signIn: (email: string, password: string) => Promise<{ data: any; error: AuthError | null }>
  signUp: (email: string, password: string, name: string) => Promise<{ data: any; error: AuthError | null }>
  signOut: () => Promise<void>
  changePassword: (newPassword: string) => Promise<{ error: AuthError | null }>
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState<UserProfile | null>(null)

  const loadProfile = useCallback(async (userId: string, retryCount = 0) => {
    console.log('[AuthContext] loadProfile called for user:', userId, 'retryCount:', retryCount)
    
    try {
      // Dodaj timeout dla getUserProfile - nie czekaj w nieskończoność
      const profilePromise = getUserProfile(userId)
      const timeoutPromise = new Promise<UserProfile | null>((_, reject) => 
        setTimeout(() => reject(new Error('getUserProfile timeout')), 5000)
      )
      const data = await Promise.race([profilePromise, timeoutPromise])
      
      // If profile is null and we haven't retried yet, try once more after a delay
      // This handles temporary network issues
      if (!data && retryCount < 1) {
        console.warn('[AuthContext] Profile not found, retrying once after delay...')
        await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1 second
        
        // Retry z timeout
        const retryPromise = getUserProfile(userId)
        const retryTimeoutPromise = new Promise<UserProfile | null>((_, reject) => 
          setTimeout(() => reject(new Error('getUserProfile retry timeout')), 5000)
        )
        const retryData = await Promise.race([retryPromise, retryTimeoutPromise])
        setProfile(retryData)
        console.log('[AuthContext] Profile retry completed:', retryData ? 'success' : 'null')
        return retryData
      }
      
      setProfile(data)
      console.log('[AuthContext] Profile loaded:', data ? 'success' : 'null')
      return data
    } catch (error) {
      const isTimeout = error instanceof Error && error.message.includes('timeout')
      if (isTimeout) {
        // Timeout nie powinien blokować UI ani "wylogowywać" profilu jeśli był już załadowany.
        console.warn('[AuthContext] loadProfile timeout - pomijam, UI nie będzie blokowane')
      } else {
        console.error('[AuthContext] Error in loadProfile:', error)
      }
      return null
    }
  }, [])

  const scheduleProfileLoad = useCallback((userId: string) => {
    if (typeof window === 'undefined') return
    // Odpal w tle (po krótkim opóźnieniu), żeby nie konkurować z krytycznymi requestami strony głównej.
    window.setTimeout(() => {
      void loadProfile(userId)
    }, 250)
  }, [loadProfile])

  useEffect(() => {
    let isMounted = true
    let isResolving = false // Flaga zapobiegająca wielokrotnym wywołaniom

    const handleSessionFailure = (error: unknown) => {
      console.error('Auth session error:', error)
      
      // Sprawdź czy to błąd związany z wygasłym tokenem lub odświeżaniem
      const isExpiredOrRefreshError = error instanceof Error && (
        error.message.includes('expired') ||
        error.message.includes('refresh') ||
        error.message.includes('token expired')
      )
      
      // Jeśli to błąd związany z wygasłym tokenem, NIE czyść sesji
      // Supabase automatycznie odświeży token używając refresh_token
      if (isExpiredOrRefreshError) {
        console.debug('Token wygasł lub w trakcie odświeżania - zachowuję sesję i czekam na odświeżenie')
        if (!isMounted) return
        setLoading(false)
        return
      }
      
      // Sprawdź czy to rzeczywisty błąd sesji (JWT, unauthorized) czy tylko problem z połączeniem
      const isRealAuthError = error instanceof Error && (
        error.message.includes('JWT') ||
        error.message.includes('unauthorized') ||
        error.message.includes('invalid token')
      )
      
      // Sprawdź czy to problem z połączeniem (network, timeout, etc.)
      const isNetworkError = error instanceof Error && (
        error.message.includes('network') ||
        error.message.includes('timeout') ||
        error.message.includes('fetch') ||
        error.message.includes('Failed to fetch')
      )
      
      if (isRealAuthError) {
        // Rzeczywisty błąd sesji - wyczyść sesję
        console.warn('Rzeczywisty błąd sesji - czyszczenie cache', error)
        clearSupabaseSessionCache()
        if (!isMounted) return
        setUser(null)
        setProfile(null)
      } else if (isNetworkError) {
        // Problem z połączeniem - zachowaj sesję
        console.warn('Problem z połączeniem - zachowuję sesję', error)
        if (!isMounted) return
        // Nie czyść sesji - użytkownik może być offline
        setLoading(false)
        return
      } else {
        // Inne błędy (np. profile loading failures) - zachowaj sesję
        console.warn('Non-auth error in auth context, keeping session:', error)
        // Don't clear user or profile, just set loading to false
      }
      setLoading(false)
    }

    const resolveInitialSession = async () => {
      // Zapobiegaj wielokrotnym wywołaniom (np. przy przełączaniu zakładek)
      if (isResolving) {
        console.debug('[AuthContext] resolveInitialSession już w trakcie - pomijam')
        return
      }
      
      console.log('[AuthContext] resolveInitialSession started')
      isResolving = true
      
      try {
        /**
         * WAŻNE:
         * Nie rób "pre-check" localStorage, bo:
         * - storageKey może się różnić (np. stare środowiska / inne projekty / cache),
         * - localStorage może chwilowo rzucić wyjątek (privacy mode / blokady),
         * - Supabase i tak ma własną logikę odtwarzania/odświeżania sesji.
         *
         * Pre-check był źródłem fałszywych "wylogowań" po odświeżeniu.
         */

        // Wywołaj getSession() - Supabase automatycznie odświeży token jeśli wygasł
        const {
          data: { session },
          error: sessionError
        } = await supabase.auth.getSession()
        
        // Obsługa błędów - NIE czyść sesji zbyt agresywnie
        if (sessionError) {
          // Sprawdź czy to błąd związany z wygasłym tokenem
          const isExpiredError = sessionError.message?.includes('expired') || 
                                sessionError.message?.includes('refresh')
          
          if (isExpiredError) {
            // Token wygasł - pozwól Supabase spróbować odświeżyć
            // Spróbuj jeszcze raz po krótkim opóźnieniu
            console.debug('Token wygasł, czekam na odświeżenie...')
            await new Promise(resolve => setTimeout(resolve, 500))
            
            // Spróbuj ponownie - Supabase powinien odświeżyć token
            const retryResult = await supabase.auth.getSession()
            
            if (retryResult.error) {
              // Nadal błąd - sprawdź czy to rzeczywisty błąd sesji
              const isRealError = retryResult.error.message?.includes('JWT') || 
                                 retryResult.error.message?.includes('unauthorized')
              
              if (isRealError) {
                console.error('Rzeczywisty błąd sesji:', retryResult.error)
                handleSessionFailure(retryResult.error)
              } else {
                // To może być tylko problem z połączeniem - zachowaj sesję
                console.warn('Problem z połączeniem, zachowuję sesję:', retryResult.error)
                if (!isMounted) {
                  isResolving = false
                  return
                }
                setLoading(false)
                isResolving = false
              }
              return
            }
            
            // Retry się powiódł - użyj nowej sesji
            const retrySession = retryResult.data.session
            if (!isMounted) {
              isResolving = false
              return
            }
            const currentUser = retrySession?.user ?? null
            setUser(currentUser)
            if (currentUser) {
              try {
                // Dodaj timeout dla loadProfile
                const profilePromise = loadProfile(currentUser.id)
                const timeoutPromise = new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Profile loading timeout')), 10000)
                )
                await Promise.race([profilePromise, timeoutPromise])
              } catch (profileError) {
                console.warn('Error loading profile, but keeping session:', profileError)
                setProfile(null)
              }
            } else {
              setProfile(null)
            }
            setLoading(false)
            isResolving = false
            return
          }
          
          // Inne błędy - sprawdź czy to rzeczywisty błąd sesji
          const isRealError = sessionError.message?.includes('JWT') || 
                             sessionError.message?.includes('unauthorized')
          
          if (isRealError) {
            console.error('Error getting session:', sessionError)
            handleSessionFailure(sessionError)
          } else {
            // To może być tylko problem z połączeniem - zachowaj sesję
            console.warn('Problem z połączeniem, zachowuję sesję:', sessionError)
            if (!isMounted) {
              isResolving = false
              return
            }
            setLoading(false)
          }
          isResolving = false
          return
        }
        
        if (!isMounted) {
          isResolving = false
          return
        }

        const currentUser = session?.user ?? null
        console.log('[AuthContext] Session resolved, user:', currentUser?.email || 'null')
        setUser(prev => {
          if (!currentUser) return null
          return prev?.id === currentUser.id ? prev : currentUser
        })
        // NIE blokuj initial render na ładowaniu profilu
        setLoading(false)

        if (currentUser) {
          console.log('[AuthContext] Scheduling profile load for user:', currentUser.id)
          scheduleProfileLoad(currentUser.id)
        } else {
          setProfile(null)
        }
        console.log('[AuthContext] resolveInitialSession completed, loading=false (profile loads in background)')
        isResolving = false
      } catch (error) {
        isResolving = false
        // Sprawdź czy to błąd związany z wygasłym tokenem
        const isExpiredError = error instanceof Error && (
          error.message.includes('expired') || 
          error.message.includes('refresh')
        )
        
        if (isExpiredError) {
          // Token wygasł - nie czyść sesji, pozwól Supabase odświeżyć
          console.debug('Token wygasł w catch, zachowuję sesję i czekam na odświeżenie')
          if (!isMounted) {
            isResolving = false
            return
          }
          setLoading(false)
          isResolving = false
          return
        }
        
        handleSessionFailure(error)
        isResolving = false
      }
    }

    resolveInitialSession()

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Handle TOKEN_REFRESHED - aktualizuj sesję, ale nie resetuj całego stanu
      if (event === 'TOKEN_REFRESHED') {
        if (!isMounted) return
        // Token został odświeżony - zaktualizuj użytkownika jeśli sesja istnieje
        if (session?.user) {
          // Nie nadpisuj obiektu user jeśli to ten sam użytkownik.
          // W przeciwnym razie komponenty zależne od `user` (np. RoleGuard) mogą chwilowo wejść w loading
          // i odmontować formularze, co powoduje utratę niezapisanych danych.
          setUser(prev => (prev?.id === session.user.id ? prev : session.user))
          // Nie odświeżaj profilu przy każdym odświeżeniu tokenu - to jest zbyt często
        }
        return
      }

      // Handle SIGNED_OUT event explicitly
      if (event === 'SIGNED_OUT') {
        if (!isMounted) return
        setUser(null)
        setProfile(null)
        setLoading(false)
        return
      }

      try {
        if (!isMounted) return
        const currentUser = session?.user ?? null
        setUser(prev => {
          if (!currentUser) return null
          return prev?.id === currentUser.id ? prev : currentUser
        })
        // NIE blokuj UI na profilu
        setLoading(false)

        if (currentUser) {
          scheduleProfileLoad(currentUser.id)
        } else {
          setProfile(null)
        }
      } catch (error) {
        // Only handle as session failure if it's a real auth error
        handleSessionFailure(error)
        // Upewnij się, że loading jest zawsze false
        setLoading(false)
      }
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [loadProfile, scheduleProfileLoad])

  const refreshProfile = useCallback(async () => {
    if (!user) {
      setProfile(null)
      return null
    }
    return loadProfile(user.id)
  }, [user, loadProfile])

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    // Log error details for debugging
    if (error) {
      console.error('Login error:', {
        message: error.message,
        status: error.status,
        name: error.name
      })
    }
    
    return { data, error }
  }

  const signUp = async (email: string, password: string, name: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
        },
      },
    })
    return { data, error }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  const changePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    })
    
    if (error) {
      console.error('Change password error:', {
        message: error.message,
        status: error.status,
        name: error.name
      })
    }
    
    return { error }
  }

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/zmien-haslo`,
    })
    
    if (error) {
      console.error('Reset password error:', {
        message: error.message,
        status: error.status,
        name: error.name
      })
    }
    
    return { error }
  }

  const value: AuthContextType = {
    user,
    profile,
    loading,
    refreshProfile,
    signIn,
    signUp,
    signOut,
    changePassword,
    resetPassword,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

